/**
 * ScheduledSendService - Manage scheduled emails
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://klorah-fast-server-my8l.onrender.com/api';

class ScheduledSendService {
  /**
   * Schedule an email for later delivery
   * @param {string} userId - User's Firebase UID
   * @param {Object} emailData - Email data (to, subject, body, etc.)
   * @param {Date} scheduledTime - When to send the email
   * @param {string} accountId - Gmail account ID
   * @returns {Promise<Object>} - Scheduled email record
   */
  async scheduleEmail(userId, emailData, scheduledTime, accountId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/scheduled-emails/schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          account_id: accountId,
          email_data: {
            to: emailData.to,
            cc: emailData.cc || [],
            bcc: emailData.bcc || [],
            subject: emailData.subject,
            body: emailData.body,
            html_body: emailData.htmlBody,
            thread_id: emailData.threadId,
            in_reply_to: emailData.inReplyTo,
            references: emailData.references,
            attachments: emailData.attachments || [],
          },
          scheduled_time: scheduledTime.toISOString(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to schedule email');
      }

      return await response.json();
    } catch (error) {
      console.error('Error scheduling email:', error);
      throw error;
    }
  }

  /**
   * Get all scheduled emails for a user
   * @param {string} userId - User's Firebase UID
   * @returns {Promise<Array>} - List of scheduled emails
   */
  async getScheduledEmails(userId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/scheduled-emails/list/${userId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to get scheduled emails');
      }

      const data = await response.json();
      return data.scheduled_emails || [];
    } catch (error) {
      console.error('Error getting scheduled emails:', error);
      throw error;
    }
  }

  /**
   * Cancel a scheduled email
   * @param {string} userId - User's Firebase UID
   * @param {string} scheduleId - Scheduled email ID
   * @returns {Promise<Object>} - Cancellation result
   */
  async cancelScheduledEmail(userId, scheduleId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/scheduled-emails/cancel/${userId}/${scheduleId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel scheduled email');
      }

      return await response.json();
    } catch (error) {
      console.error('Error cancelling scheduled email:', error);
      throw error;
    }
  }

  /**
   * Reschedule an email
   * @param {string} userId - User's Firebase UID
   * @param {string} scheduleId - Scheduled email ID
   * @param {Date} newScheduledTime - New scheduled time
   * @returns {Promise<Object>} - Updated scheduled email
   */
  async rescheduleEmail(userId, scheduleId, newScheduledTime) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/scheduled-emails/reschedule/${userId}/${scheduleId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            new_scheduled_time: newScheduledTime.toISOString(),
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reschedule email');
      }

      return await response.json();
    } catch (error) {
      console.error('Error rescheduling email:', error);
      throw error;
    }
  }

  /**
   * Send a scheduled email immediately
   * @param {string} userId - User's Firebase UID
   * @param {string} scheduleId - Scheduled email ID
   * @returns {Promise<Object>} - Send result
   */
  async sendNow(userId, scheduleId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/scheduled-emails/send-now/${userId}/${scheduleId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email now');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending email now:', error);
      throw error;
    }
  }

  /**
   * Get smart scheduling suggestions based on recipient history
   * @param {string} userId - User's Firebase UID
   * @param {string} recipientEmail - Recipient's email address
   * @returns {Promise<Object>} - Suggested send times
   */
  async getSmartSuggestions(userId, recipientEmail) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/scheduled-emails/smart-suggest`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userId,
            recipient_email: recipientEmail,
          }),
        }
      );

      if (!response.ok) {
        // Return default suggestions if smart suggestions fail
        return this.getDefaultSuggestions();
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting smart suggestions:', error);
      return this.getDefaultSuggestions();
    }
  }

  /**
   * Get default scheduling suggestions
   * @returns {Object} - Default suggestions
   */
  getDefaultSuggestions() {
    const now = new Date();
    const suggestions = [];

    // Tomorrow 9 AM
    const tomorrow9am = new Date(now);
    tomorrow9am.setDate(tomorrow9am.getDate() + 1);
    tomorrow9am.setHours(9, 0, 0, 0);
    suggestions.push({
      time: tomorrow9am.toISOString(),
      label: 'Tomorrow morning',
      reason: 'Emails sent at 9 AM have high open rates',
    });

    // Tomorrow 2 PM
    const tomorrow2pm = new Date(now);
    tomorrow2pm.setDate(tomorrow2pm.getDate() + 1);
    tomorrow2pm.setHours(14, 0, 0, 0);
    suggestions.push({
      time: tomorrow2pm.toISOString(),
      label: 'Tomorrow afternoon',
      reason: 'Post-lunch is a great time for emails',
    });

    return { suggestions };
  }
}

const scheduledSendService = new ScheduledSendService();
export default scheduledSendService;
